import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronUp, Info, Minus, Plus } from 'lucide-react';

export function A11ySectionAccordion({
  id,
  icon,
  title,
  defaultOpen = true,
  children,
}: {
  id: string;
  icon?: ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details id={id} open={defaultOpen} className="group border-b border-border-muted">
      <summary className="flex cursor-pointer list-none items-center gap-2 py-3 text-sm font-bold text-foreground marker:content-none [&::-webkit-details-marker]:hidden">
        {icon}
        <span className="flex-1">{title}</span>
        <ChevronDown size={16} className="text-muted-foreground group-open:hidden" aria-hidden />
        <ChevronUp size={16} className="hidden text-muted-foreground group-open:block" aria-hidden />
      </summary>
      <div className="pb-2">{children}</div>
    </details>
  );
}

export function A11yInfoCallout({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 flex gap-2 rounded-lg bg-amber-50/80 px-3 py-2 text-xs leading-relaxed text-muted-foreground dark:bg-amber-950/20">
      <Info size={14} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
      <p>{children}</p>
    </div>
  );
}

export function A11yToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  preview,
  badge,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  preview?: ReactNode;
  badge?: string;
}) {
  return (
    <div className="flex gap-3 border-b border-dashed border-border/80 py-4 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={id} className="text-sm font-semibold text-foreground">
            {label}
          </label>
          {badge ? (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
              {badge}
            </span>
          ) : null}
          <button
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onCheckedChange(!checked)}
            className={clsx(
              'relative ml-auto inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors sm:ml-0',
              checked ? 'bg-primary' : 'bg-muted-foreground/30',
            )}
          >
            <span
              className={clsx(
                'pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow transition-transform',
                checked ? 'translate-x-4' : 'translate-x-0',
              )}
            />
          </button>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      {preview ? (
        <div className="hidden w-16 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/30 p-2 sm:flex">
          {preview}
        </div>
      ) : null}
    </div>
  );
}

export function A11yPercentSlider({
  label,
  description,
  value,
  min,
  max,
  step = 5,
  onChange,
  onReset,
  resetLabel,
  decreaseAria,
  increaseAria,
  sliderAria,
  showPreview = true,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  onReset?: () => void;
  resetLabel?: string;
  decreaseAria: string;
  increaseAria: string;
  sliderAria: string;
  showPreview?: boolean;
}) {
  return (
    <div className="border-b border-dashed border-border/80 py-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        {onReset && resetLabel ? (
          <button type="button" onClick={onReset} className="text-xs font-medium text-primary hover:underline">
            {resetLabel}
          </button>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          aria-label={decreaseAria}
          onClick={() => onChange(value - step)}
          disabled={value <= min}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border hover:bg-muted disabled:opacity-40"
        >
          <Minus size={14} />
        </button>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-label={sliderAria}
          className="h-2 min-w-0 flex-1 cursor-pointer accent-primary"
        />
        <button
          type="button"
          aria-label={increaseAria}
          onClick={() => onChange(value + step)}
          disabled={value >= max}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border hover:bg-muted disabled:opacity-40"
        >
          <Plus size={14} />
        </button>
        {showPreview ? (
          <div
            className="hidden w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/30 py-2 text-sm font-semibold sm:flex"
            aria-hidden
          >
            Abc
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function A11ySelectRow({
  id,
  label,
  description,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="border-b border-dashed border-border/80 py-4 last:border-b-0">
      <label htmlFor={id} className="text-sm font-semibold text-foreground">
        {label}
      </label>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
      <div className="relative mt-2">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2 pr-9 text-sm font-medium focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/10"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      </div>
    </div>
  );
}

export function A11ySegmentedControl<T extends string>({
  label,
  description,
  value,
  options,
  onChange,
}: {
  label: string;
  description?: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="border-b border-dashed border-border/80 py-4 last:border-b-0">
      <p className="text-sm font-semibold text-foreground">{label}</p>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={label}>
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={clsx(
              'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
              value === opt.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-foreground hover:bg-muted',
            )}
          >
            {value === opt.value ? '✓ ' : ''}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function A11yInfoRow({
  label,
  description,
  action,
  preview,
}: {
  label: string;
  description: string;
  action?: ReactNode;
  preview?: ReactNode;
}) {
  return (
    <div className="flex gap-3 border-b border-dashed border-border/80 py-4 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      {preview ? (
        <div className="hidden w-16 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/30 p-2 sm:flex">
          {preview}
        </div>
      ) : null}
    </div>
  );
}

export function A11yCheckboxRow({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 py-2 text-xs text-foreground">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}
